import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';
import { scheduleWireResourceListCarouselArrows } from '@theme/resource-list-carousel-arrows';

class RecentlyViewedProducts extends HTMLElement {
  /** @type {boolean} */
  #loading = false;

  /** @type {boolean} */
  #reloadQueued = false;

  /** @type {number | undefined} */
  #retryTimer;

  /** @type {number} */
  #attempts = 0;

  /** @type {string} */
  #lastFingerprint = '';

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Map<string, string>} */
  #htmlCache = new Map();

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {boolean} */
  #nearViewport = false;

  /** @type {number | undefined} */
  #fallbackTimer;

  connectedCallback() {
    if (!this.hasAttribute('hidden')) {
      this.setAttribute('hidden', '');
    }

    window.addEventListener('recently-viewed:updated', this.#scheduleLoad);
    // BFCache restore only — initial load is handled by connectedCallback
    window.addEventListener('pageshow', this.#onPageShow);

    // Do NOT observe `this` while [hidden] — display:none never intersects, so the
    // section would stay hidden forever. Observe the Shopify section shell instead
    // (0-height is fine with rootMargin).
    const observeTarget =
      this.closest('.shopify-section') || this.parentElement || this;

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.#nearViewport = true;
        this.#visibilityObserver?.disconnect();
        this.#visibilityObserver = null;
        if (this.#fallbackTimer) {
          window.clearTimeout(this.#fallbackTimer);
          this.#fallbackTimer = undefined;
        }
        this.#scheduleLoad(true);
      },
      { rootMargin: '600px 0px', threshold: 0 }
    );
    this.#visibilityObserver.observe(observeTarget);

    // Safety: if IO never reports (odd layout / older browsers), still try to load.
    this.#fallbackTimer = window.setTimeout(() => {
      this.#fallbackTimer = undefined;
      if (this.#nearViewport) return;
      this.#nearViewport = true;
      this.#visibilityObserver?.disconnect();
      this.#visibilityObserver = null;
      this.#scheduleLoad(true);
    }, 3500);
  }

  disconnectedCallback() {
    window.removeEventListener('recently-viewed:updated', this.#scheduleLoad);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    if (this.#retryTimer) {
      window.clearTimeout(this.#retryTimer);
    }
    if (this.#fallbackTimer) {
      window.clearTimeout(this.#fallbackTimer);
      this.#fallbackTimer = undefined;
    }
    this.#activeFetch?.abort();
  }

  /**
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) {
      this.#lastFingerprint = '';
      this.#nearViewport = true;
      if (this.#fallbackTimer) {
        window.clearTimeout(this.#fallbackTimer);
        this.#fallbackTimer = undefined;
      }
      this.#scheduleLoad(true);
    }
  };

  /**
   * @param {boolean | Event} [immediateOrEvent]
   */
  #scheduleLoad = (immediateOrEvent) => {
    if (!this.#nearViewport) return;

    const immediate = immediateOrEvent === true;
    if (this.#retryTimer) {
      window.clearTimeout(this.#retryTimer);
      this.#retryTimer = undefined;
    }

    if (immediate) {
      delete this.dataset.loaded;
      this.#load();
      return;
    }

    // Coalesce bursty recently-viewed:updated events without a long delay
    this.#retryTimer = window.setTimeout(() => {
      this.#retryTimer = undefined;
      this.#load();
    }, 16);
  };

  async #load() {
    if (this.#loading) {
      this.#reloadQueued = true;
      return;
    }
    this.#loading = true;
    this.#reloadQueued = false;

    const sectionId = this.dataset.sectionId;
    const currentId = this.dataset.currentProductId;
    const currentHandle = this.dataset.currentProductHandle || '';
    const maxProducts = Math.max(1, Math.min(12, Number(this.dataset.maxProducts) || 8));
    const hadContent = Boolean(this.querySelector('[data-recently-viewed-grid]'));

    if (!sectionId) {
      if (!hadContent) this.setAttribute('hidden', '');
      this.#finishLoad();
      return;
    }

    try {
      const entries = RecentlyViewed.getEntries().filter((entry) => {
        if (currentId && String(entry.productId) === String(currentId)) return false;
        if (currentHandle && entry.handle && entry.handle === currentHandle) return false;
        return true;
      });

      if (entries.length === 0) {
        if (!hadContent) this.setAttribute('hidden', '');
        return;
      }

      // Small buffer for failed all_products lookups; avoid over-fetching
      const limited = entries.slice(0, Math.min(entries.length, maxProducts + 2));
      const handles = limited.map((entry) => entry.handle).filter(Boolean);
      const productIds = limited.map((entry) => entry.productId);
      // Membership fingerprint (sorted) — reorder-only updates must not remount the
      // carousel (that was snapping the slideshow back to the first card).
      const fingerprint = `${[...handles].sort().join(',')}|${[...productIds].sort().join(',')}|${maxProducts}`;

      // Skip network when the same set is already on screen
      if (this.dataset.loaded === 'true' && fingerprint === this.#lastFingerprint && hadContent) {
        return;
      }

      const html = await this.#fetchSectionHTML(sectionId, productIds, handles, fingerprint);

      if (!html) {
        this.#attempts += 1;
        if (!hadContent) this.setAttribute('hidden', '');
        if (this.#attempts < 3) {
          window.setTimeout(() => this.#scheduleLoad(true), 200 * this.#attempts);
        }
        return;
      }

      const sourceMount = this.#extractMountFromHTML(html, sectionId);

      if (!sourceMount) {
        this.#attempts += 1;
        if (!hadContent) this.setAttribute('hidden', '');
        if (this.#attempts < 3) {
          window.setTimeout(() => this.#scheduleLoad(true), 200 * this.#attempts);
        }
        return;
      }

      const mount = this.querySelector('[data-recently-viewed-mount]');
      if (!mount) {
        if (!hadContent) this.setAttribute('hidden', '');
        return;
      }

      mount.innerHTML = sourceMount.innerHTML;
      this.removeAttribute('hidden');
      this.dataset.loaded = 'true';
      this.#lastFingerprint = fingerprint;
      this.#attempts = 0;

      // Scope remount to this section — do not refresh every card on the page
      document.dispatchEvent(
        new CustomEvent('pz-collection-cards:mount', { detail: { root: this } })
      );

      const carouselRoot = this.querySelector('[data-resource-list-carousel-section]') ?? this;
      carouselRoot.querySelectorAll('[data-carousel-arrows]').forEach((container) => {
        delete container.dataset.carouselArrowsWired;
        delete container.dataset.carouselSlideshowKey;
      });
      scheduleWireResourceListCarouselArrows(carouselRoot);
      requestAnimationFrame(() => scheduleWireResourceListCarouselArrows(carouselRoot));
    } finally {
      this.#finishLoad();
    }
  }

  #finishLoad() {
    this.#loading = false;
    if (this.#reloadQueued) {
      this.#reloadQueued = false;
      this.#scheduleLoad(true);
    }
  }

  /**
   * @param {string} html
   * @param {string} sectionId
   * @returns {Element | null}
   */
  #extractMountFromHTML(html, sectionId) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const normalizedSectionId = sectionId.replace(/^shopify-section-/, '');

    const source =
      parsed.querySelector(`recently-viewed-products[id="${this.id}"]`) ||
      parsed.getElementById(`shopify-section-${normalizedSectionId}`)?.querySelector('recently-viewed-products') ||
      parsed.querySelector('recently-viewed-products');

    const sourceMount = source?.querySelector('[data-recently-viewed-mount]');
    if (!sourceMount?.querySelector('[data-recently-viewed-grid]')) {
      return null;
    }

    return sourceMount;
  }

  /**
   * @param {string} sectionId
   * @param {string[]} productIds
   * @param {string[]} handles
   * @param {string} fingerprint
   * @returns {Promise<string | null>}
   */
  async #fetchSectionHTML(sectionId, productIds, handles, fingerprint) {
    const cached = this.#htmlCache.get(fingerprint);
    if (cached) return cached;

    const requestUrls = this.#buildSectionRequestURLs(sectionId, productIds, handles);

    for (const url of requestUrls) {
      const html = await this.#requestSectionHTML(sectionId, url);
      if (html) {
        this.#htmlCache.set(fingerprint, html);
        return html;
      }
    }

    return null;
  }

  /**
   * Prefer one product-page URL with rv_handles. Search/id is last-resort fallback.
   * @param {string} sectionId
   * @param {string[]} productIds
   * @param {string[]} handles
   * @returns {URL[]}
   */
  #buildSectionRequestURLs(sectionId, productIds, handles) {
    const normalizedSectionId = sectionId.replace(/^shopify-section-/, '');
    /** @type {URL[]} */
    const urls = [];

    const pushUrl = (base, { withHandles = false, withSearch = false } = {}) => {
      try {
        const url = new URL(base, window.location.origin);
        url.searchParams.set('section_id', normalizedSectionId);
        if (withHandles && handles.length) {
          url.searchParams.set('rv_handles', handles.join(','));
        }
        if (productIds.length) {
          url.searchParams.set('rv_ids', productIds.join(','));
        }
        if (withSearch && productIds.length) {
          url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
          url.searchParams.set('type', 'product');
        }
        urls.push(url);
      } catch {
        /* skip */
      }
    };

    if (handles.length) {
      // Primary: current product path + handles → all_products[handle]
      pushUrl(window.location.pathname, { withHandles: true });
      // Last resort if all_products misses entries
      const searchUrl = RecentlyViewed.getSearchUrl() || '/search';
      pushUrl(searchUrl, { withSearch: true, withHandles: true });
    } else if (productIds.length) {
      const searchUrl = RecentlyViewed.getSearchUrl() || '/search';
      pushUrl(searchUrl, { withSearch: true });
    }

    return urls;
  }

  /**
   * Single network path via sectionRenderer (already fetches). Avoid sequential double-fetch.
   * @param {string} sectionId
   * @param {URL} url
   * @returns {Promise<string | null>}
   */
  async #requestSectionHTML(sectionId, url) {
    const normalizedSectionId = sectionId.replace(/^shopify-section-/, '');

    try {
      const html = await sectionRenderer.getSectionHTML(normalizedSectionId, false, url);
      if (html?.includes('data-recently-viewed-grid')) return html;
    } catch {
      /* fall through to direct fetch once */
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      /** @type {RequestInit} */
      const fetchInit = {
        signal: this.#activeFetch.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      };
      try {
        // @ts-ignore
        fetchInit.priority = 'high';
      } catch {
        /* ignore */
      }

      const response = await fetch(new URL(url.toString()).toString(), fetchInit);
      if (!response.ok) return null;

      const html = await response.text();
      if (!html.includes('data-recently-viewed-grid')) return null;
      return html;
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      return null;
    } finally {
      this.#activeFetch = null;
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
